import { LV_PARTS, LV_STATES } from '../../../code_export/generators/lvgl_constants';
import {
    CanvasProps,
    Display,
    EmscriptenModule,
    FileCache,
    FSNode,
    Result,
    WasmWorkerPlatformApi
} from '../../../types';
import { isAbsoluteFolderPath, normalizeFilePathForComparison, normalizePath } from '../../../utils/path';
import { wait } from '../../../utils/wait';
import { LVGLSchemaService, PropertyParamSchema, PropertySchema } from './LVGLSchemaService';
import { DEFAULT_DISPLAY_PROPS, DEFAULT_SCREEN_STYLE } from '../../../services/constants';
import { parseXmlWithDomParser } from '../../../services/xmlParser';
import { XMLValidatorCore } from './XMLValidatorCore';
import { lvglIs_9_5_0_OrHigher } from '../../../utils';

// Below types are all modified from Monaco API:
export type CoreTextModel = {
    getLineCount(): number;
    getLineLength(lineNumber: number): number;
    getWordAtPosition(position: CorePosition): CoreWordAtPosition | null;
    getLineContent(lineNumber: number): string;
    getOffsetAt(position: CorePosition): number;
    getValue(): string;
    uri: { path: string };
};

export type CoreWordAtPosition = {
    readonly word: string;
    readonly startColumn: number;
    readonly endColumn: number;
};

export type CorePosition = {
    readonly lineNumber: number;
    readonly column: number;
};

export type CoreMarkerSeverity = 'error';

// Modified from Monaco API
export type CoreMarker = {
    severity: CoreMarkerSeverity;
    message: string;
    // These are all 1-based
    startLineNumber: number;
    startColumn: number;
    endLineNumber: number;
    endColumn: number;
};

// All parsed props with their positions and information
export type PropInfo = {
    fullProp: string;
    propName: string;
    propValue: string;
    lineNumber: number;
    startColumn: number;
    endColumn: number;
};

// Other types:
export type XMLParseCallback = (content: string) => Document;

export type TestStep = {
    delay: number;
    isRuntimeWaitStep: boolean;
};

export type XmlTestRunMetadata = {
    testSteps: TestStep[];
    xmlDir: string;
    relativeXmlDir: string; // Same as 'xmlDir' but relative to the project root
};

export type XmlTestRunnerOptions = {
    runtimeInstance: EmscriptenModule;
    projectPath: string;
    xmlToProcess: string;
    testMetadata: XmlTestRunMetadata;
    platformApi: WasmWorkerPlatformApi;
    onStepResult: (index: number, result: number) => void;
    logCb: (message: string) => void;
    slowdown?: number;
    skipSaveOutputs?: boolean;
    shouldStop?: () => boolean;
};

type XmlValidationFileSystem = {
    readFileSync(path: string, options: 'utf-8'): string;
};

export type TestMetadata = {
    testSteps: TestStep[];
    xmlDir: string;
    xmlSteps: Element[];
};

export const stripXmlCommentsPreserveLines = (xml: string): string =>
    xml.replace(/<!--[\s\S]*?-->/g, (match: string) => match.replace(/[^\n\r]/g, ' '));

const LVGL_TEST_DIR: string = 'lvgl_test';

const ensureRuntimeDirectory = (runtimeInstance: EmscriptenModule, dirPath: string): void => {
    const normalizedDirPath: string = normalizePath(dirPath).replace(/^\/+|\/+$/g, '');
    if (!normalizedDirPath) return;

    let currentPath: string = '';
    for (const segment of normalizedDirPath.split('/')) {
        if (!segment) continue;
        currentPath = currentPath ? `${currentPath}/${segment}` : segment;
        if (!runtimeInstance.FS.analyzePath(currentPath).exists) {
            runtimeInstance.FS.mkdir(currentPath);
        }
    }
};

const removeRuntimeDirectoryRecursively = (runtimeInstance: EmscriptenModule, dirPath: string): void => {
    const resolvedDir: string | undefined = resolveRuntimePath(runtimeInstance, dirPath);
    if (!resolvedDir) return;

    for (const entry of runtimeInstance.FS.readdir(resolvedDir)) {
        if (entry === '.' || entry === '..') continue;
        const childPath: string = `${resolvedDir.replace(/\/$/, '')}/${entry}`;
        try {
            const stats = runtimeInstance.FS.stat(childPath);
            if (runtimeInstance.FS.isDir(stats.mode)) removeRuntimeDirectoryRecursively(runtimeInstance, childPath);
            else runtimeInstance.FS.unlink(childPath);
        } catch {
            // Ignore entries we cannot stat
        }
    }

    runtimeInstance.FS.rmdir(resolvedDir);
};

// Returns a 'CoreTextModel' from a string and a file path
export const createTextModel = (doc: string, filePath: string): (() => CoreTextModel) => {
    const value: string = doc;
    const lines: string[] = value.split('\n');

    return (): CoreTextModel => ({
        getValue: () => value,
        getLineContent: (line: number) => lines[line - 1],
        getLineCount: () => lines.length,
        getLineLength: (line: number) => lines[line - 1].length,
        getWordAtPosition: (position: CorePosition): CoreWordAtPosition => {
            const lineContent: string = lines[position.lineNumber];
            let match: RegExpExecArray | null;
            while ((match = /\b\w+\b/g.exec(lineContent)) !== null) {
                const start: number = match.index + 1;
                const end: number = start + match[0].length;
                if (position.column >= start && position.column <= end) {
                    return {
                        word: match[0],
                        startColumn: start,
                        endColumn: end
                    };
                }
            }

            return {
                word: '',
                startColumn: position.column,
                endColumn: position.column
            };
        },
        getOffsetAt: (position: CorePosition): number => {
            let offset: number = 0;
            for (let line of lines.slice(0, position.lineNumber)) offset += line.length + 1; // +1 for newline
            offset += position.column - 1;
            return offset;
        },
        uri: { path: filePath }
    });
};

// Add lvgl_test to the path= in the XML
export const rewriteStepPathsInXmlString = (xml: string): string =>
    xml.replace(
        /(<\w+[^>]*?\bpath=")([^"]+)(")/g,
        (_match, pre, path, post) => `${pre}${LVGL_TEST_DIR}/${normalizePath(path).split('/').pop()}${post}`
    );

const extractImagePathsFromXml = (xml: string, xmlDir: string): string[] => {
    const imagePaths: string[] = [];

    // Find all screenshot_compare tags and extract their path attributes
    const screenshotCompareRegex: RegExp = /<screenshot_compare[^>]*path="([^"]+)"/g;
    let match: RegExpExecArray | null;
    while ((match = screenshotCompareRegex.exec(xml)) !== null) {
        const path: string = match[1];
        // Convert to absolute path if it is relative
        const absolutePath: string = isAbsoluteFolderPath(path)
            ? path
            : `${xmlDir.replace(/[/\\]$/, '')}${xmlDir.includes('/') ? '/' : '\\'}${path}`;
        imagePaths.push(absolutePath);
    }

    return imagePaths;
};

/*
Map a generated test output file back to the folder declared in the XML.
Example: "scrolled_err.png" should be saved under "img/" if the XML references "img/scrolled.png".
*/
export const resolveOutputPathFromXml = (outputFileName: string, xmlToProcess?: string): string => {
    if (!xmlToProcess) return outputFileName;

    // Collect all screenshot reference paths from the XML so we can reuse their folder structure.
    const referencedPaths: string[] = extractImagePathsFromXml(xmlToProcess, '').map(normalizePath);
    const outputBaseName: string = outputFileName.replace(/^.*\//, '');
    // Error screenshots are named "<name>_err.png", so normalize them back to "<name>.png" for matching.
    const outputBaseStem: string = outputBaseName.replace(/_err\.png$/i, '.png');

    // Find all reference paths whose basename matches the output file.
    const matchingReferencePaths: string[] = referencedPaths.filter((referencePath: string) => {
        const referenceBaseName: string = referencePath.replace(/^.*\//, '');
        return referenceBaseName === outputBaseName || referenceBaseName === outputBaseStem;
    });

    // When no references match or multiple references share the same basename, we cannot
    // unambiguously determine the correct subfolder. Return the filename as-is to avoid
    // silently routing the output into the wrong directory.
    if (matchingReferencePaths.length !== 1) return outputFileName;

    const matchedReferencePath: string = matchingReferencePaths[0];

    // Rebuild the output path under the same relative folder used by the XML reference image.
    const referenceDir: string = matchedReferencePath.replace(/\/[^/]+$/, '');
    if (!referenceDir || referenceDir === matchedReferencePath) return outputFileName;

    return `${referenceDir}/${outputBaseName}`.replace(/^\/+/, '');
};

const getRuntimePathVariants = (path: string): string[] => {
    const cleanedPath: string = normalizePath(path).replace(/\/+$/, '');
    const pathVariants: Set<string> = new Set<string>();
    pathVariants.add(cleanedPath);
    pathVariants.add(cleanedPath.startsWith('/') ? cleanedPath.slice(1) : `/${cleanedPath}`);
    return Array.from(pathVariants);
};

const resolveRuntimePath = (runtimeInstance: EmscriptenModule, path: string): string | undefined => {
    for (const variant of getRuntimePathVariants(path)) {
        try {
            if (runtimeInstance.FS.analyzePath(variant).exists) return variant;
        } catch {
            // Continue to next variant
        }
    }
    return undefined;
};

const readRuntimeFile = (runtimeInstance: EmscriptenModule, path: string): Uint8Array | undefined => {
    for (const variant of getRuntimePathVariants(path)) {
        try {
            return runtimeInstance.FS.readFile(variant, { encoding: 'binary' });
        } catch {
            // Continue to next variant
        }
    }
    return undefined;
};

const listRuntimeFiles = (runtimeInstance: EmscriptenModule, dir: string): string[] => {
    const resolvedDir: string | undefined = resolveRuntimePath(runtimeInstance, dir);
    if (!resolvedDir) return [];

    const files: string[] = [];
    const stack: string[] = [resolvedDir];

    while (stack.length > 0) {
        const current: string = stack.pop() as string;
        let entries: string[];
        try {
            entries = runtimeInstance.FS.readdir(current);
        } catch {
            continue;
        }

        for (const entry of entries) {
            if (entry === '.' || entry === '..') continue;
            const childPath: string = `${current.replace(/\/$/, '')}/${entry}`;
            try {
                const stats = runtimeInstance.FS.stat(childPath);
                if (runtimeInstance.FS.isDir(stats.mode)) stack.push(childPath);
                else if (runtimeInstance.FS.isFile(stats.mode)) files.push(childPath);
            } catch {
                // Ignore entries we cannot stat
            }
        }
    }

    return files;
};

const listRuntimePngs = (runtimeInstance: EmscriptenModule, dir: string): string[] =>
    listRuntimeFiles(runtimeInstance, dir).filter(
        (childPath: string) => childPath.endsWith('.png') && !childPath.endsWith('_err.png')
    );

export const prepareLvglTestFolder = async (
    runtimeInstance: EmscriptenModule,
    relativeXmlDir: string,
    xmlToProcess: string,
    logCb: (message: string) => void
): Promise<void> => {
    logCb('Preparing LVGL Test Folder...');

    // Clear existing lvgl_test folder
    if (runtimeInstance.FS.analyzePath(LVGL_TEST_DIR).exists) {
        removeRuntimeDirectoryRecursively(runtimeInstance, LVGL_TEST_DIR);
    }
    // Create new lvgl_test folder
    runtimeInstance.FS.mkdir(LVGL_TEST_DIR);

    // Extract image paths referenced in XML
    const cleanedXmlDir: string = normalizePath(relativeXmlDir);
    const referencedImagePaths: string[] = extractImagePathsFromXml(xmlToProcess, relativeXmlDir).map(normalizePath);

    // Read PNGs directly from the runtime virtual FS
    const pngs: string[] = listRuntimePngs(runtimeInstance, cleanedXmlDir)
        .map(normalizePath)
        .filter((pngPath: string) =>
            referencedImagePaths.some(
                (refPath) =>
                    pngPath === refPath || pngPath.endsWith(refPath.replace(cleanedXmlDir, '').replace(/^[/\\]/, ''))
            )
        );

    logCb?.(`Found ${referencedImagePaths.length} referenced images in XML, ${pngs.length} matching PNG files exist`);

    for (const path of pngs) {
        const filename: string = path.replace(/^.*[/\\]/, '');
        if (!filename) continue;
        const content: Uint8Array | undefined = readRuntimeFile(runtimeInstance, path);
        if (!content) {
            logCb?.(`Unable to load image data for ${path}`);
            continue;
        }

        if (await pngIsOutOfDateOrCorrupt(runtimeInstance, content, filename, logCb)) {
            // We refrain from copying the PNG if it is out-of-date or corrupt . . .
            // so the LVGL test framework will regenerate it
        } else {
            // Essentially just copying a file already in the Virtual FileSystem into the tests folder
            const byteArray: Uint8Array =
                content instanceof Uint8Array ? content : new Uint8Array(content as ArrayBufferLike);
            runtimeInstance.FS.writeFile(`${LVGL_TEST_DIR}/${filename}`, byteArray);
        }
    }
};

const isNodeEnv: boolean = typeof process !== 'undefined' && process.release?.name === 'node';

const pngIsOutOfDateOrCorrupt = async (
    runtimeInstance: EmscriptenModule,
    content: Uint8Array,
    filename: string,
    logCb: (message: string) => void
): Promise<boolean> => {
    const runtimeWidth: number | undefined = runtimeInstance.Module?.canvas?.width || runtimeInstance.canvas?.width;
    const runtimeHeight: number | undefined = runtimeInstance.Module?.canvas?.height || runtimeInstance.canvas?.height;

    let imageWidth: number | undefined;
    let imageHeight: number | undefined;

    if (isNodeEnv || typeof Image === 'undefined') {
        const byteArray: Uint8Array =
            content instanceof Uint8Array ? content : new Uint8Array(content as ArrayBufferLike);
        try {
            const dimensions: Omit<CanvasProps, 'style' | 'target'> | null = readPngDimensions(byteArray);
            if (dimensions) {
                imageWidth = dimensions.width;
                imageHeight = dimensions.height;
            } else {
                logCb(`Unable to read PNG dimensions for ${filename}; copying without validation.`);
                return false;
            }
        } catch (error: unknown) {
            logCb?.(
                `Failed to read PNG metadata for ${filename} in Node environment: ${
                    error instanceof Error ? error.message : String(error)
                }`
            );
            runtimeInstance.FS.writeFile(`${LVGL_TEST_DIR}/${filename}`, byteArray);
        }
    } else {
        // Check if image resolution matches runtime instance resolution
        const image: HTMLImageElement = new Image();
        const imageLoaded = new Promise<void>((resolve, reject) => {
            image.onload = () => resolve();
            image.onerror = () => reject(new Error(`Failed to load image: ${filename}`));
        });
        image.src = `data:image/png;base64,${btoa(String.fromCharCode(...content))}`;

        try {
            await imageLoaded;
            imageWidth = image.width;
            imageHeight = image.height;
        } catch (error: unknown) {
            logCb?.(`Failed to load image ${filename}: ${error instanceof Error ? error.message : String(error)}`);
            return true;
        }
    }

    // PNG is out-of-date, it's resolution does not match the Preview
    if (imageWidth !== runtimeWidth || imageHeight !== runtimeHeight) {
        return true;
    }

    return false;
};

const readPngDimensions = (data: Uint8Array): Omit<CanvasProps, 'style' | 'target'> | null => {
    if (data.length < 24) return null;
    const signature: number[] = [137, 80, 78, 71, 13, 10, 26, 10];
    for (let i = 0; i < signature.length; i++) if (data[i] !== signature[i]) return null;
    const view: DataView = new DataView(data.buffer, data.byteOffset, data.byteLength);
    const width: number = view.getUint32(16);
    const height: number = view.getUint32(20);
    return { width, height };
};

export const registerTests = (runtimeInstance: EmscriptenModule, xmlToRun: string): void => {
    if (!runtimeInstance.lvrt_xml_test_run_init) {
        throw new Error('The runtime instance does not have "lvrt_xml_test_run_init" function.');
    }

    if (runtimeInstance.lvrt_xml_test_register_from_data && runtimeInstance.lvrt_xml_test_run_init) {
        runtimeInstance.lvrt_xml_test_register_from_data(xmlToRun, 'A:');
        runtimeInstance.lvrt_xml_test_run_init();
    }
};

export const saveTestOutputs = async (
    runtimeInstance: EmscriptenModule,
    xmlDir: string,
    relativeXmlDir: string,
    platformApi: WasmWorkerPlatformApi,
    xmlToProcess?: string
): Promise<void> => {
    const normalizedRelativeXmlDir: string = normalizePath(relativeXmlDir); // Must be POXIX-style for the runtime FS
    const files: string[] = runtimeInstance.FS.readdir(LVGL_TEST_DIR);

    for (const file of files) {
        if (file === '.' || file === '..') continue;
        const resolvedOutputPath: string = resolveOutputPathFromXml(file, xmlToProcess);

        const content: Uint8Array = runtimeInstance.FS.readFile(`${LVGL_TEST_DIR}/${file}`, { encoding: 'binary' });

        ensureRuntimeDirectory(runtimeInstance, `${normalizedRelativeXmlDir}`);
        // Write the file into the Virtual FileSystem. If it was created by running the tests
        // (it is an _err file or was created because the reference was missing), it will be
        // in the LVGL_TEST_DIR folder in the VFS, but not the source folder.
        runtimeInstance.FS.writeFile(`${normalizedRelativeXmlDir}/${resolvedOutputPath}`, content, {
            encoding: 'binary'
        });
        // Write the file to the user's filesystem - the platform should handle directory creation automatically
        await platformApi.writeBinaryFile(`${xmlDir}/${resolvedOutputPath}`, content);
    }
};

export const extractTestMetadata = (
    xmlToProcess: string,
    currentTabPath: string,
    parseXml: XMLParseCallback
): TestMetadata => {
    const xmlDoc: Document = parseXml(xmlToProcess);
    const xmlSteps: Element[] = Array.from(xmlDoc.querySelectorAll('steps > *'));
    const testSteps: TestStep[] = xmlSteps.map((element: Element): TestStep => ({
        delay: parseInt(element.getAttribute('ms') || '20'),
        isRuntimeWaitStep: element.tagName === 'wait'
    }));
    const xmlDir: string = currentTabPath.replace(/[/\\][^/\\]*$/, ''); // The folder path of the test XML file
    return { testSteps, xmlDir, xmlSteps };
};

// Performs a single test step
export const runStep = async (
    step: TestStep,
    runtimeInstance: EmscriptenModule,
    slowdown: number = 0
): Promise<number> => {
    if (!runtimeInstance.lvrt_xml_test_run_next) {
        throw new Error('The runtime instance does not have "lvrt_xml_test_run_next" function.');
    }
    try {
        const result: number = runtimeInstance.lvrt_xml_test_run_next(slowdown);
        // <wait ms="..."> is already delayed by the runtime; JS delay only paces other step types.
        if (slowdown > 0 && !step.isRuntimeWaitStep) await wait(step.delay * slowdown);

        return result === 0 || result === 1 ? result : -1;
    } catch (error: unknown) {
        console.error('Error running next test step:', error);
        throw error;
    }
};

const yieldToEventLoop = (): Promise<void> => wait(0);

export const runSteps = async (
    steps: TestStep[],
    runtimeInstance: EmscriptenModule,
    onStepResult: (index: number, result: number) => void,
    logCb: (message: string) => void,
    slowdown: number = 0,
    shouldStop: () => boolean = () => false
): Promise<number[]> => {
    const results: number[] = [];
    for (let i = 0; i < steps.length; i++) {
        if (shouldStop()) break;
        const result: number = await runStep(steps[i], runtimeInstance, slowdown);
        results[i] = result;
        onStepResult(i, result);
        logCb(`Step ${i + 1}/${steps.length} result: ${result}`);
        if (shouldStop()) break;
        if (i < steps.length - 1) await yieldToEventLoop();
    }

    return results;
};

export const runFullXmlTestSuite = async ({
    runtimeInstance,
    xmlToProcess,
    testMetadata,
    platformApi,
    onStepResult,
    logCb,
    slowdown = 0,
    shouldStop = () => false
}: XmlTestRunnerOptions): Promise<number[]> => {
    const { xmlDir, testSteps, relativeXmlDir } = testMetadata;
    const processedXml: string = rewriteStepPathsInXmlString(xmlToProcess);

    await prepareLvglTestFolder(runtimeInstance, relativeXmlDir, xmlToProcess, logCb);
    registerTests(runtimeInstance, processedXml);

    let results: number[] = [];

    try {
        results = await runSteps(testSteps, runtimeInstance, onStepResult, logCb, slowdown, shouldStop);
    } finally {
        try {
            if (runtimeInstance.lvrt_xml_test_run_stop) runtimeInstance.lvrt_xml_test_run_stop();
            else throw 'There is no "lvrt_xml_test_run_stop" registered!';
        } catch (error: unknown) {
            console.error('Error stopping run:', error);
        }
    }

    if (shouldStop()) {
        logCb('Test run stopped.');
        return results;
    }

    console.debug('Test run complete, saving outputs...');

    try {
        await saveTestOutputs(runtimeInstance, xmlDir, relativeXmlDir, platformApi, xmlToProcess);
    } catch (error: unknown) {
        console.error('Error saving test outputs:', error);
        throw error;
    }

    return results;
};

// TODO: Handle this in a cleaner and more generic way
// [Architectural Improvement]
export const parseLocalStyles = (xml: string): string[] => {
    const stylesRegex: RegExp = /<styles[^>]*>([\s\S]*?)<\/styles>/g;
    const styleTagRegex: RegExp = /<style\s+[^>]*name="([^"]+)"/g;
    let localStyleNames: string[] = [];
    let stylesMatch: RegExpExecArray | null;
    while ((stylesMatch = stylesRegex.exec(xml)) !== null) {
        let stylesContent: string = stylesMatch[1];
        let styleMatch: RegExpExecArray | null;
        while ((styleMatch = styleTagRegex.exec(stylesContent)) !== null) localStyleNames.push(styleMatch[1]);
    }
    return localStyleNames;
};

export const getValidStyles = (xml: string, schemaService: LVGLSchemaService): string[] =>
    Array.from(schemaService.globalStyles.keys()).concat(parseLocalStyles(xml));

// Just because this matches doesn't mean the prop is a prop with a selector.
// Param syntax is also hyphenated and would match this regex,
// but we can validate that separately by checking if the prop schema has params.
export const propIsInSelectorSyntax = (propName: string): boolean => propName.match(/^[a-z_]+(-[a-z]+)+$/) !== null;

export const removeSelectorsFromPropName = (propName: string): string => propName.replace(/-[a-z-]+$/, '');

export const getSelectorsFromPropName = (propName: string): string[] => {
    const selectors: string[] | undefined = propName
        .match(/^[a-z_]+((?:-[a-z]+)+)$/)
        ?.slice(1)
        ?.flatMap((l) => l.split('-'))
        .filter(Boolean);
    return selectors || [];
};

const validLvParts = LV_PARTS.map((p) => p.toLowerCase()).filter((p) => p !== 'any');
const validLvStates = LV_STATES.map((s) => s.toLowerCase()).filter((s) => s !== 'any');

// Ensures the property is valid for its element
export const validateProperty = (
    schemaService: LVGLSchemaService,
    prop: PropInfo,
    markers: CoreMarker[],
    allValidProperties: string[],
    parentElement: string,
    elementName: string,
    extendedElement: string | undefined
): string => {
    let propNameToUse: string = prop.propName;

    // selector syntax for lvgl 9.5.0+
    if (lvglIs_9_5_0_OrHigher(LVGLSchemaService.LVGLVersion)) {
        const isSelectorSyntax = propIsInSelectorSyntax(prop.propName);
        const directPropertyResult = isSelectorSyntax
            ? schemaService.getProperty(elementName, prop.propName, extendedElement, parentElement)
            : undefined;
        const directPropSchema: PropertySchema | PropertyParamSchema | undefined = directPropertyResult?.[0];
        const isDirectSchemaProperty: boolean =
            allValidProperties.includes(prop.propName) &&
            !(directPropSchema as PropertyParamSchema | undefined)?.isParam;
        const cleanedPropName = removeSelectorsFromPropName(prop.propName);

        // Validate selectors, must do first (the next validation block assumes selector syntax has been handled)
        if (isSelectorSyntax && !isDirectSchemaProperty) {
            // Check if this prop allows selector syntax
            const result = schemaService.getProperty(elementName, prop.propName, extendedElement, parentElement);
            const propSchema: PropertySchema | PropertyParamSchema | undefined = result?.[0];
            if (!propSchema) {
                markers.push({
                    severity: 'error',
                    message: `Cannot find schema for attribute '${cleanedPropName}' for element '${elementName}'${
                        extendedElement ? ` (extends ${extendedElement})` : ''
                    }`,
                    startLineNumber: prop.lineNumber,
                    startColumn: prop.startColumn,
                    endLineNumber: prop.lineNumber,
                    endColumn: prop.startColumn + prop.propName.length
                });
                return cleanedPropName;
            }

            // Prop is in selector syntax, but this prop doesn't allow it.
            // (And it also isn't a params, which also have a hyphenated syntax.)
            if (!(propSchema as PropertySchema)?.selector && !(propSchema as PropertyParamSchema)?.isParam) {
                markers.push({
                    severity: 'error',
                    message: `Attribute '${cleanedPropName}' is using selector syntax but does not support it`,
                    startLineNumber: prop.lineNumber,
                    startColumn: prop.startColumn,
                    endLineNumber: prop.lineNumber,
                    endColumn: prop.startColumn + prop.propName.length
                });
                return cleanedPropName;
            }

            propNameToUse = cleanedPropName;
            // Only validate selectors if the prop actually supports them.
            // Otherwise, we assume hyphenated syntax is for params and validate that separately.
            if ((propSchema as PropertySchema)?.selector) {
                const selectors: string[] = getSelectorsFromPropName(prop.propName);

                let cumulativePropLengthMarker: number = propNameToUse.length + 1;

                // Validate selectors
                selectors.forEach((sel) => {
                    if (!validLvParts.includes(sel) && !validLvStates.includes(sel)) {
                        markers.push({
                            severity: 'error',
                            message: `Invalid selector '${sel}' for property '${propNameToUse}'`,
                            startLineNumber: prop.lineNumber,
                            startColumn: prop.startColumn + cumulativePropLengthMarker,
                            endLineNumber: prop.lineNumber,
                            endColumn: prop.startColumn + cumulativePropLengthMarker + sel.length
                        });
                    }

                    cumulativePropLengthMarker += sel.length + 1;
                });
            }
        }
    }

    if (!allValidProperties.includes(propNameToUse)) {
        markers.push({
            severity: 'error',
            message: `Unknown attribute '${propNameToUse}' for element '${elementName}'${
                extendedElement ? ` (extends ${extendedElement})` : ''
            }`,
            startLineNumber: prop.lineNumber,
            startColumn: prop.startColumn,
            endLineNumber: prop.lineNumber,
            endColumn: prop.startColumn + prop.propName.length
        });
    }

    return propNameToUse;
};

export const debounce = <T extends (...args: any[]) => void>(
    func: T,
    delay: number
): ((...args: Parameters<T>) => void) => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
};

export const collectXmlFiles = (directoryData: FSNode[]): FSNode[] => {
    const xmlFiles: FSNode[] = [];

    for (const file of directoryData) {
        if (file.type === 'directory' && file.children) {
            xmlFiles.push(...collectXmlFiles(file.children));
        } else if (file.type === 'file' && file.path.endsWith('.xml')) {
            xmlFiles.push(file);
        }
    }

    return xmlFiles;
};

const getCodegenPathAliases = (path: string): string[] => {
    const normalizedPath: string = normalizeFilePathForComparison(path);
    const theiaPath: string = normalizedPath.replace(/^([A-Za-z]:\/)/, '/$1');
    return Array.from(new Set([path, normalizedPath, theiaPath]));
};

const MAX_XML_VALIDATION_ERRORS: number = 10;

export const validateXmlFilesToCache = async (
    directoryData: FSNode[],
    schemaService: LVGLSchemaService,
    fs: XmlValidationFileSystem,
    fileCache: FileCache,
    logCb: (message: string) => void
): Promise<Result> => {
    const xmlFiles: FSNode[] = collectXmlFiles(directoryData);
    const validationErrors: string[] = [];

    logCb('Validating code...');
    logCb(`Found ${xmlFiles.length} XML files.`);

    for (const file of xmlFiles) {
        let content: string;
        try {
            content = fs.readFileSync(file.path, 'utf-8');
        } catch (error: unknown) {
            validationErrors.push(
                `${file.path}: ${error instanceof Error ? error.message : String(error ?? 'Unknown error')}`
            );
            continue;
        }

        const markers: CoreMarker[] = XMLValidatorCore(createTextModel(content, file.path)(), schemaService, (xml) => {
            const document: Document = parseXmlWithDomParser(xml);
            getCodegenPathAliases(file.path).forEach((path: string) => {
                fileCache[path] = document;
            });
            return document;
        });

        validationErrors.push(
            ...markers.map(
                (marker: CoreMarker): string =>
                    `${file.path}:${marker.startLineNumber}:${marker.startColumn} ${marker.message}`
            )
        );
    }

    if (validationErrors.length) {
        const shownErrors: string[] = validationErrors.slice(0, MAX_XML_VALIDATION_ERRORS);
        const remainingCount: number = validationErrors.length - shownErrors.length;
        const suffix: string = remainingCount > 0 ? `\n    ...and ${remainingCount} more error(s)` : '';

        return {
            success: false,
            error: `Validation error(s):\n    ${shownErrors.join('\n    ')}${suffix}`
        };
    }

    logCb('Code Validation passed');
    return { success: true, error: null };
};

// This can be used to parse a display from either a 'display' tag in `project.xml` or a
// `preview` tag from widgets/components
export const parseDisplay = (parent: Element, displayElement: Element | undefined, index: number): Display => {
    const name: string = parent.getAttribute('name') ?? displayElement?.getAttribute('name') ?? '';

    // Parse `style_` and `styles` from the display
    const styleAttrs: Record<string, string> = Array.from(displayElement?.attributes || []).reduce(
        (acc: Record<string, string>, attr) => {
            if (attr.name.startsWith('style_') || attr.name === 'styles') {
                acc[attr.name] = attr.value;
            }
            return acc;
        },
        {}
    );

    // Convert radius attribute to style_radius (display only)
    const radius: string | null | undefined = displayElement?.getAttribute('radius');
    if (radius) styleAttrs['style_radius'] = radius;

    // When parsing previews, it is important to fallback to an empty string, so
    // higher code can fill the target in with the default project target
    const fallbackTargetName: string = parent.tagName === 'target' ? name : '';

    return {
        id: index,
        name,
        width: parseInt(displayElement?.getAttribute('width') || DEFAULT_DISPLAY_PROPS.width.toString()),
        height: parseInt(displayElement?.getAttribute('height') || DEFAULT_DISPLAY_PROPS.height.toString()),
        zoom:
            parseInt(displayElement?.getAttribute('zoom') || ((DEFAULT_DISPLAY_PROPS.zoom ?? 1) * 100).toString()) /
            100,
        language: displayElement?.getAttribute('language') ?? DEFAULT_DISPLAY_PROPS.language,
        // TODO: fallback for target to first project target
        // This is tricky because the parsing function needs to have access to that first target
        target: { name: displayElement?.getAttribute('use_target') || fallbackTargetName, id: 1 << (index + 1) },
        colorFormat: displayElement?.getAttribute('color_format') ?? '',
        style: {
            ...DEFAULT_SCREEN_STYLE,
            ...styleAttrs
        }
    };
};
